import { cloneDeep } from "lodash";
import {
  getRequest,
  postRequest,
  putRequest
} from "@/views/academy/api/apiRequests";
import { PlayerRegistrationApiEndpoints } from "@/views/academy/playerregistration/PlayerRegistrationApiEndpoints";
import { prepareQueryParams } from "@/helpers/prepareQueryParams";
import { PaymentProvidersTypes } from "@/views/academy/enums/PaymentProvidersTypes";
import {
  ApiRequestHeaderTypes,
  ApiRequestHeaderValueTypes
} from "@/views/academy/enums/ApiRequestHeaderTypes";
import {
  RegistrationQuestionsAddressFields,
  RegistrationQuestionsTitleAdminContext,
  RegistrationQuestionsTitlePlayerContext
} from "@/views/academy/enums/RegistrationQuestionsTitle";
import { uuid as vueUuid } from "vue-uuid";
import { RegistrationAnswerType } from "@/views/academy/enums/AnswerType";
import axios from "axios";
import { bus } from "@/main";
import { i18n } from "@/plugins/i18n";

const state = () => ({
  playerProfileForm: [],
  teamRegistration: {},
  playerRegistration: {},
  playersToRegistration: [],
  playerRegistrationDetails: {},
  playerRegistrationDetailsLoader: false,
  fansAndFollowers: {
    added: [],
    updated: [],
    deleted: []
  },
  playerRegistrationSummary: {},
  registrationPlayerId: "",
  playerRegistrationInfo: {},
  playerRegistrationAddOns: [],
  stripePaymentIntentDetails: {},
  playerAvatar: {
    imageUrl: null,
    avatarUploadError: null,
    file: null,
    isFileUploading: false
  },
  isMoveToPlayerRegistrationFormPaymentDetailsDisable: false,
  paypalCheckoutPaymentValues: null,
  allEventOptionsAtCapacity: false,
  userNotInvited: false
});

const getters = {
  playerProfileForm: (state: { playerProfileForm: {} }) =>
    state.playerProfileForm,

  teamRegistration: (state: { teamRegistration: string }) =>
    state.teamRegistration,

  playerRegistration: (state: { playerRegistration: string }) =>
    state.playerRegistration,

  playersToRegistration: (state: { playersToRegistration: [] }) =>
    state.playersToRegistration,

  playerRegistrationDetails: (state: { playerRegistrationDetails: {} }) =>
    state.playerRegistrationDetails,

  playerRegistrationDetailsLoader: (state: {
    playerRegistrationDetailsLoader: boolean;
  }) => state.playerRegistrationDetailsLoader,

  fansAndFollowers: (state: { fansAndFollowers: {} }) => state.fansAndFollowers,

  playerRegistrationSummary: (state: { playerRegistrationSummary: {} }) =>
    state.playerRegistrationSummary,

  registrationPlayerId: (state: { registrationPlayerId: string }) =>
    state.registrationPlayerId,

  playerRegistrationInfo: (state: { playerRegistrationInfo: {} }) =>
    state.playerRegistrationInfo,

  playerRegistrationAddOns: (state: { playerRegistrationAddOns: [] }) => {
    return [...state.playerRegistrationAddOns].sort(
      (a: any, b: any) => b.isEditMode - a.isEditMode
    );
  },

  stripePaymentIntentDetails: (state: { stripePaymentIntentDetails: {} }) =>
    state.stripePaymentIntentDetails,

  playerAvatar: (state: { playerAvatar: object }) => state.playerAvatar,

  isMoveToPlayerRegistrationFormPaymentDetailsDisable: (state: {
    isMoveToPlayerRegistrationFormPaymentDetailsDisable: boolean;
  }) => state.isMoveToPlayerRegistrationFormPaymentDetailsDisable,

  paypalCheckoutPaymentValues: (state: {
    paypalCheckoutPaymentValues: object;
  }) => state.paypalCheckoutPaymentValues,

  allEventOptionsAtCapacity: (state: { allEventOptionsAtCapacity: boolean }) =>
    state.allEventOptionsAtCapacity,

  userNotInvited: (state: { userNotInvited: boolean }) => state.userNotInvited
};

const mutations = {
  setPlayerProfileFormValues: (
    state: { playerProfileForm: {} },
    payload: { playerProfileForm: {} }
  ) => {
    state.playerProfileForm = cloneDeep(payload.playerProfileForm);
  },

  setTeamRegistration: (
    state: { teamRegistration: {} },
    payload: { teamRegistration: {} }
  ) => {
    state.teamRegistration = payload.teamRegistration;
  },

  setPlayerRegistration: (
    state: { playerRegistration: {} },
    payload: { playerRegistration: {} }
  ) => {
    state.playerRegistration = payload.playerRegistration;
  },

  setPlayersToRegistration: (
    state: { playersToRegistration: {} },
    payload: { playersToRegistration: {} }
  ) => {
    state.playersToRegistration = payload.playersToRegistration;
  },

  setPlayerRegistrationDetails: (
    state: { playerRegistrationDetails: {} },
    payload: { playerRegistrationDetails: {} }
  ) => {
    state.playerRegistrationDetails = payload.playerRegistrationDetails;
  },

  setPlayerRegistrationDetailsLoader: (
    state: { playerRegistrationDetailsLoader: boolean },
    payload: { playerRegistrationDetailsLoader: boolean }
  ) => {
    state.playerRegistrationDetailsLoader =
      payload.playerRegistrationDetailsLoader;
  },

  setFansAndFollowers: (
    state: { fansAndFollowers: {} },
    payload: { fansAndFollowers: {} }
  ) => {
    state.fansAndFollowers = payload.fansAndFollowers;
  },

  setPlayerRegistrationSummary: (
    state: { playerRegistrationSummary: {} },
    payload: { playerRegistrationSummary: {} }
  ) => {
    state.playerRegistrationSummary = payload.playerRegistrationSummary;
  },

  setRegistrationPlayerId: (
    state: { registrationPlayerId: "" },
    payload: { registrationPlayerId: "" }
  ) => {
    state.registrationPlayerId = payload.registrationPlayerId;
  },

  setPlayerRegistrationInfo: (
    state: { playerRegistrationInfo: {} },
    payload: { playerRegistrationInfo: {} }
  ) => {
    state.playerRegistrationInfo = payload.playerRegistrationInfo;
  },

  setPlayerRegistrationAddOns: (
    state: { playerRegistrationAddOns: [] },
    payload: { playerRegistrationAddOns: [] }
  ) => {
    state.playerRegistrationAddOns = payload.playerRegistrationAddOns;
  },

  setStripePaymentIntentDetails: (
    state: { stripePaymentIntentDetails: {} },
    payload: { stripePaymentIntentDetails: {} }
  ) => {
    state.stripePaymentIntentDetails = payload.stripePaymentIntentDetails;
  },

  setPlayerAvatar: (
    state: { playerAvatar: object },
    payload: { name: string; value: string }
  ) => {
    const { name, value } = payload;
    state.playerAvatar = { ...state.playerAvatar, [name]: value };
  },

  clearPlayerAvatar: (state: { playerAvatar: object | null }) => {
    state.playerAvatar = {
      imageUrl: null,
      avatarUploadError: null,
      file: null,
      isFileUploading: false
    };
  },

  setMoveToPlayerRegistrationFormPaymentDetailsDisable: (
    state: {
      isMoveToPlayerRegistrationFormPaymentDetailsDisable: boolean;
    },
    payload: { isMoveToPlayerRegistrationFormPaymentDetailsDisable: boolean }
  ) => {
    state.isMoveToPlayerRegistrationFormPaymentDetailsDisable =
      payload.isMoveToPlayerRegistrationFormPaymentDetailsDisable;
  },

  setPaypalCheckoutPaymentValues: (
    state: { paypalCheckoutPaymentValues: object },
    payload: { paypalCheckoutPaymentValues: object }
  ) => {
    state.paypalCheckoutPaymentValues = payload.paypalCheckoutPaymentValues;
  },

  setAllEventOptionsAtCapacity: (
    state: { allEventOptionsAtCapacity: boolean },
    payload: { allEventOptionsAtCapacity: boolean }
  ) => {
    state.allEventOptionsAtCapacity = payload.allEventOptionsAtCapacity;
  },
  setUserNotInvited: (
    state: { userNotInvited: boolean },
    payload: { userNotInvited: boolean }
  ) => {
    state.userNotInvited = payload.userNotInvited;
  }
};

const actions = {
  fetchRegistrationLink: async (
    context: any,
    payload: {
      registrationId: string;
      orgUnitId: string;
      successCallback: Function;
    }
  ) => {
    const { registrationId, orgUnitId, successCallback } = payload;

    const query = prepareQueryParams({ orgUnitId });

    try {
      const res: any = await getRequest(
        `${PlayerRegistrationApiEndpoints.FETCH_REGISTRATION_LINK}/${registrationId}${query}`
      );
      if (res.status === "success") {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  clearPlayerRegistrationDetails: async (context: any) => {
    context.commit("setPlayerRegistrationDetails", {
      playerRegistrationDetails: {}
    });
  },

  fetchPlayerRegistrationDetails: async (
    context: any,
    payload: {
      registrationId: string;
      playerId: string;
    }
  ) => {
    context.commit("setPlayerRegistrationDetailsLoader", {
      playerRegistrationDetailsLoader: true
    });

    const { registrationId, playerId } = payload;

    try {
      const res: any = await getRequest(
        `${PlayerRegistrationApiEndpoints.FETCH_PLAYER_REGISTRATION_DETAILS}/${registrationId}/player/${playerId}`
      );
      if (res.status === "success") {
        context.commit("setPlayerRegistrationDetails", {
          playerRegistrationDetails: res.data
        });
        context.commit("setPlayerRegistrationDetailsLoader", {
          playerRegistrationDetailsLoader: false
        });
        context.commit("setPlayerAvatar", {
          name: "imageUrl",
          value: `data:image/jpeg;base64,${res.data.avatar}`
        });
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchPlayersToRegistration: async (
    context: any,
    payload: {
      registrationId: string;
    }
  ) => {
    const { registrationId } = payload;

    const query = prepareQueryParams({ registrationId });

    try {
      const res: any = await getRequest(
        `${PlayerRegistrationApiEndpoints.FETCH_PLAYERS_TO_REGISTRATION}/${query}`
      );

      if (res.status === "success") {
        context.commit("setPlayersToRegistration", {
          playersToRegistration: res.data
        });
      }
    } catch (e) {
      console.log(e);
    }
  },

  savePlayerRegistrationDetails: async (
    context: any,
    payload: {
      registrationId: string;
      playerId: string;
      body: any;
      successCallback: Function;
    }
  ) => {
    const {
      registrationId,
      playerId,
      body: reqBody,
      successCallback
    } = payload;
    const body = {
      avatarId: context.getters.playerAvatar.avatarId || null,
      ...reqBody,
      addOnFormResponses: context.getters?.playerRegistrationAddOns
        .filter(
          (item: { isEditMode: boolean; quantity: number }) =>
            item.isEditMode && item.quantity
        )
        .map(
          (item: {
            addOnId: string;
            quantity: string;
            registrationPlayerAddOnFormContentList: [];
          }) => {
            const {
              addOnId,
              quantity,
              registrationPlayerAddOnFormContentList
            } = item;
            return {
              addOnId,
              quantity,
              registrationPlayerAddOnFormContentList: registrationPlayerAddOnFormContentList
                ? registrationPlayerAddOnFormContentList?.filter(
                    (item: { playerAnswer: any }) => item.playerAnswer
                  )
                : []
            };
          }
        )
    };

    try {
      const endpoint =
        context.getters.playerRegistration.value === "new_player"
          ? `${PlayerRegistrationApiEndpoints.SAVE_NEW_PLAYER_REGISTRATION_DETAILS}/${registrationId}`
          : `${PlayerRegistrationApiEndpoints.SAVE_PLAYER_REGISTRATION_DETAILS}/${registrationId}/player/${playerId}`;
      const res: any = await postRequest(endpoint, body);

      if (res.status === "success") {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchPlayerRegistrationSummary: async (
    context: any,
    payload: {
      registrationPlayerId: string;
      successCallback: Function;
    }
  ) => {
    const { registrationPlayerId, successCallback } = payload;

    try {
      const res: any = await getRequest(
        `${PlayerRegistrationApiEndpoints.FETCH_PLAYER_REGISTRATION_SUMMARY}/${registrationPlayerId}`
      );
      if (res.status === "success") {
        context.commit("setPlayerRegistrationSummary", {
          playerRegistrationSummary: res.data
        });
        if (
          context.getters.playerRegistrationSummary.paymentMethod ===
          PaymentProvidersTypes.STRIPE
        ) {
          context.dispatch("createStripePaymentIntent", {
            successCallback: ({ data }: any) => {
              context.commit("setStripePaymentIntentDetails", {
                stripePaymentIntentDetails: data
              });
            }
          });
        }
        successCallback && successCallback();
      }
    } catch (e) {
      console.log(e);
    }
  },

  savePlayerRegistrationPromoCodeAndPaymentPlan: async (
    context: any,
    payload: {
      registrationPlayerId: string;
      promoCode: string;
      userPickedPaymentPlan: boolean;
      successCallback: Function;
      failureCallback: Function;
    }
  ) => {
    const {
      registrationPlayerId,
      promoCode,
      userPickedPaymentPlan,
      successCallback,
      failureCallback
    } = payload;
    const query = prepareQueryParams({ promoCode, userPickedPaymentPlan });
    try {
      const res: any = await postRequest(
        `${PlayerRegistrationApiEndpoints.SAVE_PLAYER_REGISTRATION_PROMO_CODE_AND_PAYMENT_PLAN}/${registrationPlayerId}${query}`
      );
      if (res.status === "success") {
        context.commit("setPlayerRegistrationSummary", {
          playerRegistrationSummary: res.data
        });
        successCallback && successCallback();
      } else {
        failureCallback && failureCallback(res.response);
      }
    } catch (e) {
      console.log(e);
    }
  },

  createStripePaymentIntent: async (
    context: any,
    payload: {
      successCallback: Function;
    }
  ) => {
    const { successCallback } = payload;
    try {
      const registrationPlayerId = context.getters.registrationPlayerId;
      const res: any = await getRequest(
        `${PlayerRegistrationApiEndpoints.CREATE_STRIPE_PAYMENT_INTENT}/${registrationPlayerId}`
      );
      successCallback && successCallback(res);
    } catch (e) {
      console.log(e);
    }
  },

  authorizePayPalPayment: async (context: any) => {
    context.commit("setPaypalCheckoutPaymentValues", {
      paypalCheckoutPaymentValues: null
    });
    try {
      const registrationPlayerId = context.getters.registrationPlayerId;
      const res: any = await getRequest(
        `${PlayerRegistrationApiEndpoints.AUTHORIZE_PAYPAL_PAYMENT}/${registrationPlayerId}`
      );
      context.commit("setPaypalCheckoutPaymentValues", {
        paypalCheckoutPaymentValues: res.data
      });
    } catch (e) {
      console.log(e);
    }
  },

  finalizePaymentRegistration: async (
    context: any,
    payload: {
      successCallback: Function;
    }
  ) => {
    const { successCallback } = payload;
    try {
      const registrationPlayerId = context.getters.registrationPlayerId;
      const res: any = await postRequest(
        `${PlayerRegistrationApiEndpoints.FINALIZE_PLAYER_REGISTRATION}/${registrationPlayerId}/finalize`
      );
      if (res.status === "success") {
        successCallback && (await successCallback(res));
      }
    } catch (e) {
      console.log(e);
    }
  },

  fetchPlayerRegistrationInfo: async (
    context: any,
    payload: {
      registrationId: string;
      orgUnitId: string;
    }
  ) => {
    const { registrationId, orgUnitId } = payload;
    const query = prepareQueryParams({ orgUnitId });
    try {
      const res: any = await axios
        .get(
          `/rest-api/${PlayerRegistrationApiEndpoints.FETCH_PLAYER_REGISTRATION_INFO}/${registrationId}/info${query}`
        )
        .then(res => res.data)
        .catch(err => err.response?.data);
      if (
        res.status === "error" &&
        res.code === "USER_NOT_INVITED_TO_REGISTRATION"
      ) {
        context.commit("setUserNotInvited", {
          userNotInvited: true
        });
      } else if (res.status === "error") {
        bus.$emit("errorSnack", i18n.tc("common.error"));
      } else if (res.status === "success") {
        context.commit("setPlayerRegistrationInfo", {
          playerRegistrationInfo: res.data
        });
        const playerRegistrationInfo = context.getters.playerRegistrationInfo;
        const playerProfileForm: any = [];
        res.data.questions.forEach((item: any) => {
          if (
            item.questionTitle ===
            RegistrationQuestionsTitleAdminContext.PLAYER_ADDRESS
          ) {
            for (
              let i = 0;
              i <= RegistrationQuestionsAddressFields.length - 1;
              i++
            ) {
              playerProfileForm.push({
                ...item,
                questionTitle: RegistrationQuestionsAddressFields[i]
              });
            }
          } else {
            playerProfileForm.push(item);
          }
        });
        context.commit("setPlayerProfileFormValues", {
          playerProfileForm: playerProfileForm?.map((item: any) => {
            const {
              id,
              answerType,
              answers,
              description,
              optional,
              questionTitle
            } = item;

            let label;
            const {
              FIRST_NAME: AFN,
              LAST_NAME: ALN,
              PLAYER_HOME_PHONE,
              PLAYER_MOBILE_PHONE
            } = RegistrationQuestionsTitleAdminContext;
            const {
              FIRST_NAME: PFN,
              LAST_NAME: PLN,
              ZIP_CODE
            } = RegistrationQuestionsTitlePlayerContext;

            const {
              NUMBER,
              WAIVER,
              SECTION_TITLE,
              TEXT_BLOCK,
              SIGNATURE_BLOCK,
              DATE
            } = RegistrationAnswerType;

            switch (questionTitle) {
              case AFN:
                label = PFN;
                break;
              case ALN:
                label = PLN;
                break;
              default:
                label = questionTitle;
            }
            return {
              id,
              type: answerType,
              items: answers,
              description,
              optional,
              label: [
                WAIVER,
                SECTION_TITLE,
                TEXT_BLOCK,
                SIGNATURE_BLOCK
              ].includes(answerType)
                ? ""
                : label,
              name: questionTitle,
              value: "",
              rules: {
                ...(!optional
                  ? {
                      required: {
                        value: 0
                      }
                    }
                  : {}),
                maxLength: {
                  value: 255
                },
                ...([WAIVER, SIGNATURE_BLOCK].includes(answerType)
                  ? {
                      exactMatch: {
                        value: `${playerRegistrationInfo?.currentlyLoggedUserFirstName} ${playerRegistrationInfo?.currentlyLoggedUserLastName}`
                      }
                    }
                  : {}),
                ...([PLAYER_HOME_PHONE, PLAYER_MOBILE_PHONE].includes(
                  questionTitle
                )
                  ? {
                      phoneNumberLength: 9
                    }
                  : {}),
                ...(questionTitle === ZIP_CODE
                  ? {
                      specificLength: {
                        value: 5
                      }
                    }
                  : {})
              },
              medium:
                [NUMBER, DATE].includes(answerType) ||
                [
                  RegistrationQuestionsTitlePlayerContext.STATE,
                  RegistrationQuestionsTitlePlayerContext.ZIP_CODE
                ].includes(questionTitle)
            };
          })
        });
        res.data.addons.forEach((item: any) => {
          const isEditMode = item.customerMustPurchaseAtLeast > 0;

          context.commit("setPlayerRegistrationAddOns", {
            playerRegistrationAddOns: [
              ...context.getters.playerRegistrationAddOns,
              {
                ...item,
                // addOnOptionList: item.addOnOptionList.map(option => {
                //   // console.log(option);
                //   // console.log(
                //   //   this.playerRegistrationDetails.playerAddOnList[0].registrationPlayerAddOnForm
                //   // );
                //   // const playerOption = this.playerRegistrationDetails.playerAddOnList[0].registrationPlayerAddOnForm.find(
                //   //   playerAddOn =>
                //   //     (playerAddOn.registrationAddOnFormContentId =
                //   //       option.id)
                //   // );
                //   // console.log(playerOption)
                //   return {
                //     ...option,
                //     value: "s"
                //   };
                // }),
                id: vueUuid.v4(),
                isEditMode,
                required: isEditMode,
                quantity: 0
              }
            ]
          });

          if (isEditMode) {
            context.commit("setPlayerRegistrationAddOns", {
              playerRegistrationAddOns: [
                ...context.getters.playerRegistrationAddOns,
                {
                  ...item,
                  id: vueUuid.v4(),
                  isEditMode: false,
                  required: false
                }
              ]
            });
          }
        });
      }
    } catch (e) {
      console.log(e);
    }
  },

  uploadPlayerAvatar: async (
    context: any,
    payload: {
      playerId: string;
      successCallback: Function;
    }
  ) => {
    const { playerId, successCallback } = payload;
    const query = prepareQueryParams({ playerId });

    const formData = new FormData();
    formData.append("file", context.getters.playerAvatar.file);
    const config = {
      headers: {
        [ApiRequestHeaderTypes.CONTENT_TYPE]:
          ApiRequestHeaderValueTypes.MULTIPART_FORM_DATA
      },
      timeout: 0
    };

    try {
      const res: any = await postRequest(
        `${PlayerRegistrationApiEndpoints.UPLOAD_PLAYER_AVATAR}${query}`,
        formData,
        config
      );
      if (res.status === "success") {
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  uploadNewPlayerAvatar: async (
    context: any,
    payload: {
      successCallback: Function;
    }
  ) => {
    const { successCallback } = payload;

    const formData = new FormData();
    formData.append("file", context.getters.playerAvatar.file);
    const config = {
      headers: {
        [ApiRequestHeaderTypes.CONTENT_TYPE]:
          ApiRequestHeaderValueTypes.MULTIPART_FORM_DATA
      },
      timeout: 0
    };

    try {
      const avatarId = context.getters.playerAvatar.avatarId;
      const res: any = avatarId
        ? await putRequest(
            `${PlayerRegistrationApiEndpoints.UPLOAD_NEW_PLAYER_AVATAR}?avatarId=${avatarId}`,
            formData,
            config
          )
        : await postRequest(
            PlayerRegistrationApiEndpoints.UPLOAD_NEW_PLAYER_AVATAR,
            formData,
            config
          );
      if (res.status === "success") {
        context.commit("setPlayerAvatar", {
          name: "avatarId",
          value: res.data.avatarId
        });
        successCallback && successCallback(res);
      }
    } catch (e) {
      console.log(e);
    }
  },

  updatePaypalCheckoutPaymentStatus: async (
    context: any,
    payload: {
      body: {
        paymentStatus: string;
        email: string;
      };
    }
  ) => {
    const registrationPlayerId = context.getters.registrationPlayerId;

    try {
      await postRequest(
        `${PlayerRegistrationApiEndpoints.UPDATE_PAYPAL_CHECKOUT_PAYMENT_STATUS}/${registrationPlayerId}/status`,
        payload.body
      );
    } catch (e) {
      console.log(e);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
